'use client';

import { IconButton } from 'BomdisoftComponents/buttons/icon';
import { MoreVertRoundedIcon } from 'BomdisoftComponents/icons/more-vert-rounded';
import { Menu } from 'BomdisoftComponents/menu/index';
import { Tooltip } from 'BomdisoftComponents/tooltip/index';
import { rowActionsT, type SupportedLanguage } from '@bomdisoft/translations';
import { type ReactNode, useId, useRef, useState } from 'react';

export type RowMenuProps = {
  children: ReactNode;
  language: SupportedLanguage;
  testId: string;
};

/**
 * The kebab the approved record puts at the end of every grid row.
 *
 * **Owns its own anchor and open state**, one instance per row, because a menu
 * hoisted to the grid would have to track which row opened it and would reopen
 * against a stale anchor after a sort or a filter.
 *
 * **No click inside here reaches the row, and the wrapper is why.** The menu is a
 * popover but it still renders INSIDE the grid row, so a choice bubbles up to
 * `onRowClick` like any other click in that row. Stopping it on the button alone
 * was not enough and made "Open details" a no-op: the item set the open row and
 * the bubbled row click toggled it straight back off, which reads as a menu item
 * that does nothing. One wrapper around both the trigger and the menu, so a
 * caller cannot add an item that forgets.
 */
export const RowMenu = ({ children, language, testId }: RowMenuProps): ReactNode => {
  const anchor = useRef<HTMLButtonElement>(null);
  const guard = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const label = rowActionsT[language];

  return (
    /* A REACT handler, not a native listener. React delegates every event to the
       root container, so a native `stopPropagation` partway up the tree stops
       the event before React dispatches ANY of its handlers, including the menu
       item's own: the first attempt at this guard silenced the whole menu. A
       synthetic handler runs in React's own bubble, after the item it wraps and
       before the row behind it, which is exactly the window this needs.

       The two rules below are suppressed rather than satisfied because they are
       right about markup in general and wrong about this element: it adds no
       interaction of its own, it removes one. A key handler here would invent a
       keyboard affordance for a span nobody can focus. */
    // biome-ignore lint/a11y/noStaticElementInteractions: a propagation guard, not an interactive element; the button and the menu items inside carry the interaction.
    // biome-ignore lint/a11y/useKeyWithClickEvents: there is no action to trigger, so there is no keyboard equivalent to add.
    // biome-ignore lint/a11y/noNoninteractiveElementInteractions: same element, same reason; making it a button would put a second control in every row.
    <span onClick={event => event.stopPropagation()} ref={guard}>
      <Tooltip placement='left' title={label}>
        <IconButton aria-controls={menuId} aria-label={label} data-testid={testId} onClick={() => setOpen(current => !current)} ref={anchor} size='small'>
          <MoreVertRoundedIcon size={18} />
        </IconButton>
      </Tooltip>
      <Menu anchorRef={anchor} id={menuId} label={label} onClose={() => setOpen(false)} open={open} testId={`${testId}-menu`}>
        {children}
      </Menu>
    </span>
  );
};
